import { useEffect, useState, useMemo, useCallback, memo } from "react";
import { Outlet, useLocation } from "react-router-dom";
import ContactFormHeader from "../components/ContractForm/ContractFormHeader";
import QuestionnairesFormHeader from "../components/Questionnaires/QuestionnairesFormHeader";
import ContactHeader from "../components/Create-Contract/ContactHeader";
import CreateInvoiceHeader from "../components/CreateInvoice/CreateInvoiceHeader";
import EmailHeader from "../components/Email/EmailHeader";
import CustomerEventHeader from "../components/Events/Customer-Event/CustomerEventHeader/CustomerEventHeader";
import GalleryTopHeader from "../components/Gallery/GalleryTopHeader";
import PackageHeader from "../components/Package/PackageHeader";
import ProductHeader from "../components/Products/ProductHeader";
import Header from "../components/Shared/Header/Header";
import Sidebar from "../components/Shared/Sidebar/Sidebar";

/**
 * Optimized DashboardLayout with memoized route checks
 */
function DashboardLayout() {
  const { pathname } = useLocation();

  // Memoize all route checks to prevent recalculation on every render
  const layoutConfig = useMemo(() => {
    const noHeader =
      pathname === "/create-contract" ||
      pathname === "/create-invoice" ||
      pathname.includes("/edit-contract") ||
      pathname.includes("/edit-invoice") ||
      pathname.includes("/edit-proposals") ||
      pathname === "/create-proposals";

    const nosidebar =
      pathname === "/gallery" ||
      pathname === "/gallery/settings" ||
      pathname.includes("/gallery/settings/") ||
      pathname === "/create-contract" ||
      pathname.includes("/edit-contract") ||
      pathname === "/create-invoice" ||
      pathname.includes("/edit-invoice") ||
      pathname === "/create-proposals" ||
      pathname.includes("/edit-proposals") ||
      pathname === "/new-questionnaires" ||
      pathname.includes("/edit-questionnaires") ||
      pathname === "/new-contact" ||
      pathname.includes("/edit-contact") ||
      pathname === "/create-email-template" ||
      pathname.includes("/edit-email-template");

    // Determine which header to show
    let headerType = "default";
    if (pathname === "/customer-events") headerType = "customerEvent";
    else if (pathname === "/gallery" || pathname === "/gallery/settings" || 
             pathname.includes("/gallery/settings/") || pathname === "/gallery/preview") headerType = "gallery";
    else if (pathname === "/add-package" || pathname.includes("/edit-package")) headerType = "package";
    else if (pathname === "/add-product" || pathname.includes("/edit-product")) headerType = "product";
    else if (pathname === "/create-proposals" || pathname === "/edit-proposals" || noHeader) headerType = "contact";
    else if (pathname === "/create-invoice") headerType = "invoice";
    else if (pathname === "/new-contact" || pathname.includes("/edit-contact")) headerType = "contractForm";
    else if (pathname === "/new-questionnaires" || pathname.includes("/edit-questionnaires")) headerType = "questionnaires";
    else if (pathname === "/create-email-template" || pathname.includes("/edit-email-template")) headerType = "email";

    return { noHeader, nosidebar, headerType };
  }, [pathname]);

  // State for managing the sidebar's open/close status
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Removed artificial loader - let Suspense handle loading states

  // Memoize callbacks to prevent unnecessary re-renders
  const toggleSidebar = useCallback(() => setSidebarOpen(prev => !prev), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        sidebarOpen &&
        !e.target.closest("#sidebar") &&
        !e.target.closest(".sidebar-toggler")
      ) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [sidebarOpen]);

  // Memoize header component selection
  const HeaderComponent = useMemo(() => {
    const headerProps = { toggleSidebar, closeSidebar };
    
    switch (layoutConfig.headerType) {
      case "customerEvent":
        return <CustomerEventHeader {...headerProps} />;
      case "gallery":
        return <GalleryTopHeader {...headerProps} />;
      case "package":
        return <PackageHeader {...headerProps} />;
      case "product":
        return <ProductHeader {...headerProps} />;
      case "contact":
        return <ContactHeader {...headerProps} />;
      case "invoice":
        return <CreateInvoiceHeader {...headerProps} />;
      case "contractForm":
        return <ContactFormHeader {...headerProps} />;
      case "questionnaires":
        return <QuestionnairesFormHeader {...headerProps} />;
      case "email":
        return <EmailHeader {...headerProps} />;
      default:
        return <Header toggleSidebar={toggleSidebar} closeSidebar={closeSidebar} />;
    }
  }, [layoutConfig.headerType, toggleSidebar, closeSidebar]);

  return (
    <div
      id="mainsidebar"
      className={`position-relative bg-white d-flex p-0 ${
        sidebarOpen ? "sidebar-open" : ""
      }`}
    >
      {/* Overlay (click to close on mobile) */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar}></div>
      )}

      {/* Sidebar */}
      {!layoutConfig.nosidebar && (
        <Sidebar sidebarOpen={sidebarOpen} closeSidebar={closeSidebar} />
      )}

      {/* ========= Main Content Area ========= */}
      <div
        id="maincontent"
        className={`content ${sidebarOpen ? "open" : ""}`}
        style={
          layoutConfig.nosidebar
            ? { marginLeft: "0px", width: "100%", transition: "none" }
            : {}
        }
      >
        {/* Headers */}
        {HeaderComponent}

        {/* Main Page Content */}
        <div
          className={`container-fluid overflow-x-hidden ${
            !layoutConfig.noHeader && "p-lg-5 p-md-5"
          } ${layoutConfig.noHeader && "bg-white"}`}
        >
          <div className="row g-lg-5 g-md-5 g-3 align-items-center justify-content-center">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(DashboardLayout);
